import { memo, useCallback, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBuildPageHeader,
  MiButton,
  MiDateRangePicker,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  ICONS_WEB,
} from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { OrderActivityDrawer } from "@mibuildweb/components";
import { Box, Divider } from "@mui/material";
import { useOrderTemplates } from "../../hooks/useOrderTemplate/useOrderTemplates";
import { OrderTemplatesFilter } from "../Orders/OrderTemplatesFilter/OrderTemplatesFilter";
import { AccountingList } from "./AccountingList/AccountingList";
import { AccountingProvider, useAccountingContext } from "./AccountingProvider";
import {
  ACCOUNTING_DATE_PICKER_CALENDER_COUNT,
  ACCOUNTING_DATE_PICKER_MAX_SELECTION_DAYS,
} from "./accountingConstants";
import { useAccountingViewModel } from "./useAccountingViewModel";

/**
 * The `Accounting` component is a page that displays and manages order invoices and prebill.
 * It includes features such as date range filtering, tabbed navigation, and a list of orderTemplates.
 *
 * @component
 * @returns {JSX.Element} The rendered `Accounting` component.
 */
const AccountingContent = () => {
  const {
    activeTab,
    orderCardConfig,
    orderHoverCardConfig,
    filters,
    tabs,
    refresh,
    orders,
    debouncedSearch,
    isLoading,
    stateManager,
    search,
    orderTypeCount,
    setOrders,
    getOrders,
    setSearch,
    isDateRangeDisabled,
    getOrderCardConfig,
    handleFilterChange,
    handleResetDateRange,
    handleRefresh,
    handleTabChange,
    getOrderTypesCount,
    getInvoices,
  } = useAccountingViewModel();
  const { orderTemplates, getOrderTemplates } = useOrderTemplates();
  const { classes, theme } = useStyles();

  const {
    isOrderActivityDrawerOpen,
    closeDrawer,
    selectedOrder,
    getOrderActivity,
    setSelectedOrder,
  } = useAccountingContext();

  const handleSearch = useCallback(
    (e) => setSearch(e.target.value),
    [setSearch]
  );

  useEffect(() => {
    getOrderCardConfig();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [activeTab, filters]);

  useEffect(() => {
    setOrders([]);
  }, [activeTab]);

  useEffect(() => {
    getOrderTypesCount();
  }, [activeTab, refresh, debouncedSearch]);

  useEffect(() => {
    getOrderTemplates();
  }, []);

  return (
    <>
      <MiBuildPageHeader
        title="Invoices"
        search={search}
        onHandleSearch={handleSearch}
        loading={isLoading}
      />
      <MiTab
        tabs={tabs}
        currentTab={activeTab.value}
        onTabChange={handleTabChange}
      />

      <Box className={classes.listContainer} margin={theme.spacing(3, 3, 3, 3)}>
        <OrderTemplatesFilter
          orderTemplates={orderTemplates}
          filters={filters}
          onFilterChange={handleFilterChange}
          orderTypeCount={orderTypeCount}
        />
        <Divider orientation="vertical" flexItem sx={{ paddingLeft: "8px" }} />
        <Box display="flex" flexDirection="column" flex={1} overflow="auto">
          {activeTab.dateRange && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              my={2}
              gap={2}
            >
              <MiDateRangePicker
                showShortCutItems
                showThisWeekButton={false}
                dateRange={filters.dateRange}
                onDateChange={(startDate, endDate) => {
                  const normalizedEndDate =
                    !endDate && startDate ? startDate : endDate;

                  handleFilterChange({
                    dateRange: { startDate, endDate: normalizedEndDate },
                  });
                }}
                calendarCount={ACCOUNTING_DATE_PICKER_CALENDER_COUNT}
                maxSelectionEnd={ACCOUNTING_DATE_PICKER_MAX_SELECTION_DAYS}
              />
              <MiButton
                title="Reset"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                startIcon={{
                  path: mdi[ICONS_WEB.CALENDAR_REFRESH],
                }}
                onClick={handleResetDateRange}
                disabled={isDateRangeDisabled()}
                disableRipple
                data-testid="reset-button"
              />
            </Box>
          )}
          <AccountingList
            activeTab={activeTab}
            orderTemplates={orders}
            orderCardConfig={orderCardConfig}
            orderHoverCardConfig={orderHoverCardConfig}
            refresh={refresh}
            isLoading={isLoading}
            stateManager={stateManager}
            setOrderTemplates={setOrders}
            getOrderTemplates={getOrders}
            getInvoices={getInvoices}
            search={debouncedSearch}
            refreshPagination={activeTab}
            onRefresh={handleRefresh}
          />
        </Box>
        <OrderActivityDrawer
          open={isOrderActivityDrawerOpen}
          onClose={closeDrawer}
          order={selectedOrder}
          getOrderActivity={getOrderActivity}
          onRefresh={(order) => {
            setSelectedOrder(order);
            handleRefresh();
          }}
        />
      </Box>
    </>
  );
};

export const Accounting = memo(() => {
  return (
    <AccountingProvider>
      <AccountingContent />
    </AccountingProvider>
  );
});

const useStyles = makeStyles()((theme) => ({
  mainContainer: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
  },
  listContainer: {
    position: "relative",
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.common.white,
    boxShadow: "0px 1px 2px 0px rgba(0,0,0,.2)",
    overflow: "hidden",
    padding: 8,
    display: "flex",
    flex: 1,
  },
}));
